import React, { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import Editor from '@monaco-editor/react';
import { Box, CircularProgress } from '@mui/material';
import { registerFlexLanguage, FLEX_LANGUAGE_ID } from '../../monaco/flexLanguage';

/**
 * FlexEditor - Monaco Editor wrapper for FLEX DSL
 *
 * Features:
 * - FLEX syntax highlighting
 * - Error markers from diagnostics
 * - Keyboard shortcuts (Ctrl+S save, F5 run)
 */
const FlexEditor = forwardRef(function FlexEditor({
  value,
  onChange,
  onSave,
  onRun,
  diagnostics = [],
  readOnly = false,
  themeMode = 'dark',
  height = '100%',
  language = null,
  path = null,
}, ref) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  // onSave/onRun are read through refs (kept current every render) rather
  // than captured directly in the addCommand closures below, since
  // handleEditorDidMount only fires once per Monaco instance -- without
  // this, switching tabs would keep invoking the FIRST tab's save/run
  // handlers instead of whichever tab is actually focused now.
  const onSaveRef = useRef(onSave);
  const onRunRef = useRef(onRun);
  onSaveRef.current = onSave;
  onRunRef.current = onRun;

  // Expose goToLine method to parent via ref
  useImperativeHandle(ref, () => ({
    goToLine(line, column = 1) {
      if (editorRef.current) {
        editorRef.current.revealLineInCenter(line);
        editorRef.current.setPosition({ lineNumber: line, column });
        editorRef.current.focus();
      }
    },
  }), []);

  // Handle editor mount
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Register FLEX language
    registerFlexLanguage(monaco);

    // Set the theme
    monaco.editor.setTheme(themeMode === 'dark' ? 'flex-dark' : 'flex-light');

    // Add keyboard shortcuts -- read via refs so these always call whichever
    // tab is currently active, not the tab that was open at mount time.
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      if (onSaveRef.current) {
        onSaveRef.current();
      }
    });

    editor.addCommand(monaco.KeyCode.F5, () => {
      if (onRunRef.current) {
        onRunRef.current();
      }
    });

    // Focus the editor
    editor.focus();
  }, [themeMode]);

  // Update theme when themeMode changes
  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(themeMode === 'dark' ? 'flex-dark' : 'flex-light');
    }
  }, [themeMode]);

  // Update markers when diagnostics change
  useEffect(() => {
    if (editorRef.current && monacoRef.current) {
      const monaco = monacoRef.current;
      const model = editorRef.current.getModel();

      if (model) {
        const markers = diagnostics.map((diag) => ({
          severity: diag.severity === 'error'
            ? monaco.MarkerSeverity.Error
            : diag.severity === 'warning'
              ? monaco.MarkerSeverity.Warning
              : monaco.MarkerSeverity.Info,
          startLineNumber: diag.line || 1,
          startColumn: diag.column || 1,
          endLineNumber: diag.endLine || diag.line || 1,
          endColumn: diag.endColumn || (diag.column ? diag.column + 10 : 100),
          message: diag.message,
          source: 'flex',
        }));

        monaco.editor.setModelMarkers(model, 'flex', markers);
      }
    }
  }, [diagnostics]);

  // Editor options
  const options = {
    minimap: { enabled: false },
    fontSize: 14,
    fontFamily: "'IBM Plex Mono', 'Fira Code', 'Consolas', monospace",
    lineNumbers: 'on',
    renderLineHighlight: 'all',
    scrollBeyondLastLine: false,
    wordWrap: 'off',
    tabSize: 2,
    insertSpaces: true,
    automaticLayout: true,
    readOnly,
    padding: { top: 16, bottom: 16 },
    scrollbar: {
      vertical: 'auto',
      horizontal: 'auto',
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
    },
    suggest: {
      showKeywords: true,
      showSnippets: true,
    },
    quickSuggestions: {
      other: true,
      comments: false,
      strings: false,
    },
  };

  // Loading component
  const loading = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        bgcolor: themeMode === 'dark' ? '#09090b' : '#fafafa',
      }}
    >
      <CircularProgress sx={{ color: '#22c55e' }} />
    </Box>
  );

  return (
    <Box sx={{ height, width: '100%' }}>
      <Editor
        height={height}
        language={language || FLEX_LANGUAGE_ID}
        value={value}
        path={path}
        onChange={onChange}
        onMount={handleEditorDidMount}
        options={options}
        loading={loading}
        theme={themeMode === 'dark' ? 'flex-dark' : 'flex-light'}
      />
    </Box>
  );
});

export default FlexEditor;
